/** /otas — global Online_Travel_Agency catalog (grid + create dialog + import
 *  wizard + email-mapping preview). OTAs are GLOBAL, so there is no tenant
 *  picker or tenant filter anywhere on this page. Boolean columns render as
 *  compact centered check indicators; the less-used ones are hidden by default. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Chip, CircularProgress, Link, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as otasApi from '@/api/otas';
import type { OTAListItem } from '@/api/otas';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { EmailMappingPreviewDialog } from './EmailMappingPreviewDialog';
import { OTACreateDialog } from './OTACreateDialog';
import { OtaImportWizard } from './OtaImportWizard';

/** URL `enabled` value → API boolean. `true`/`false` pass through; anything else
 *  drops the filter (any). */
const enabledToApi = (v: string): boolean | undefined =>
  v === 'true' ? true : v === 'false' ? false : undefined;

const FILTERS: FilterDef[] = [{ param: 'enabled', toApi: enabledToApi }];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id',
  'name',
  'enabled',
  'sequence',
  'domain_count',
  'format_overrides_count',
  'unique_emails',
  'plus_supported',
  'direct',
  'app_promotion',
  'review_url',
  'contact_email',
  'contact_phone',
];
const DEFAULT_HIDDEN = [
  'format_overrides_count',
  'unique_emails',
  'plus_supported',
  'app_promotion',
  'contact_email',
  'contact_phone',
];

export function OTAsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_ota_manage');
  const canView = hasPerm('console_ota_view');
  const canImport = hasPerm('console_bg_import_ota');

  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));
  const [importOpen, setImportOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  const presets = useGridPresets({
    grid: 'otas',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'sequence',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const boolCol = useCallback(
    (field: string, headerName: string): GridColDef<OTAListItem> => ({
      field,
      headerName,
      width: 90,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) =>
        p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
    }),
    [],
  );

  const columns = useMemo<GridColDef<OTAListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/otas/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      boolCol('enabled', 'Enabled'),
      { field: 'sequence', headerName: 'Seq', width: 80, align: 'center', headerAlign: 'center' },
      {
        field: 'domain_count',
        headerName: 'Domains',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        valueGetter: (_, row) => row.domain_count ?? 0,
      },
      {
        field: 'format_overrides_count',
        headerName: 'Fmt overrides',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        description: 'Channels this OTA enforces a rendering format for (edit on the detail page).',
        renderCell: (p) => {
          const n = p.row.format_overrides_count ?? 0;
          return n > 0 ? (
            <Chip size="small" variant="outlined" color="warning" label={n} />
          ) : null;
        },
      },
      boolCol('unique_emails', 'Unique emails'),
      boolCol('plus_supported', 'Plus'),
      boolCol('direct', 'Direct'),
      boolCol('app_promotion', 'App promo'),
      {
        field: 'review_url',
        headerName: 'Review',
        width: 110,
        sortable: false,
        renderCell: (p) =>
          p.row.review_url ? (
            <Link
              href={p.row.review_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Link
            </Link>
          ) : null,
      },
      {
        field: 'contact_email',
        headerName: 'Contact email',
        width: 200,
        sortable: false,
        valueGetter: (_, row) => row.contact_email ?? '',
      },
      {
        field: 'contact_phone',
        headerName: 'Contact phone',
        width: 160,
        // Not in the backend _ORDERABLE set — display-only.
        sortable: false,
        valueGetter: (_, row) => row.contact_phone ?? '',
      },
    ],
    [boolCol],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      otasApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'sequence',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const enabledValue = getParam('enabled') ?? 'any';
  const toolbarItems: FilterBarItem[] = [
    {
      key: 'enabled',
      priority: 5,
      active: enabledValue !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledValue}
          sx={{ width: 170 }}
          onChange={(e) => setParam('enabled', e.target.value === 'any' ? null : e.target.value)}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="otas-registry"
        title="OTAs"
        subtitle="Online travel agencies — global catalog of booking sources and the email-domain rules that attribute inbound mail to each."
        actions={
          <>
            {canView && (
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => setEmailPreviewOpen(true)}
              >
                Email mapping preview
              </Button>
            )}
            {canImport && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setImportOpen(true)}
              >
                Import OTAs
              </Button>
            )}
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New OTA
              </Button>
            )}
          </>
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<OTAListItem>
          queryKey={['otas']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="otas"
          batchActions={[deleteBatchAction('console_ota_manage'), batchEditAction('otas', 'OTA')]}
          defaultSort={{ field: 'sequence', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          emptyMessage="No OTAs yet."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or domain',
            exportFileName: 'otas',
            presets: presets.toolbar,
          }}
        />
      )}

      {createOpen && <OTACreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />}
      {importOpen && <OtaImportWizard open={importOpen} onClose={() => setImportOpen(false)} />}
      {emailPreviewOpen && (
        <EmailMappingPreviewDialog
          open={emailPreviewOpen}
          onClose={() => setEmailPreviewOpen(false)}
        />
      )}
    </Box>
  );
}
